import { useState } from "react";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  OnlinePaymentProvider,
  OnlinePaymentStatus,
  SellerOrderDetail,
} from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { useOrderPaymentTransactionQuery } from "@/modules/orders/services/useOrderPaymentTransactionQuery";
import { RefundSheet } from "./RefundSheet";

const PROVIDER_BRAND_KEY: Record<OnlinePaymentProvider, string> = {
  paymob: keys.paymentGateway.brandPaymob,
  paytabs: keys.paymentGateway.brandPaytabs,
  kashier: keys.paymentGateway.brandKashier,
  fawry: keys.paymentGateway.brandFawry,
  stripe: keys.paymentGateway.brandStripe,
};

const STATUS_KEY: Record<OnlinePaymentStatus, string> = {
  initiated: keys.orders.onlinePayStatusInitiated,
  pending: keys.orders.onlinePayStatusPending,
  paid: keys.orders.onlinePayStatusPaid,
  failed: keys.orders.onlinePayStatusFailed,
  refunded: keys.orders.onlinePayStatusRefunded,
  cancelled: keys.orders.onlinePayStatusCancelled,
  expired: keys.orders.onlinePayStatusExpired,
};

// Status → design token for the badge dot + text.
const STATUS_TONE: Record<OnlinePaymentStatus, string> = {
  paid: "var(--success-500)",
  refunded: "var(--text-secondary)",
  failed: "var(--danger-500)",
  cancelled: "var(--text-muted)",
  expired: "var(--text-muted)",
  pending: "var(--warning-500)",
  initiated: "var(--warning-500)",
};

type OnlinePaymentPanelProps = {
  order: SellerOrderDetail;
  storeSlug: string;
};

// Online-payment panel on the order drawer: gateway + status + amount, and a
// Refund action for a paid transaction. Renders nothing for COD/manual orders
// (no online transaction).
export function OnlinePaymentPanel({
  order,
  storeSlug,
}: OnlinePaymentPanelProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [refundOpen, setRefundOpen] = useState(false);
  const { transaction } = useOrderPaymentTransactionQuery({
    storeSlug,
    orderId: order.id,
  });

  if (!transaction) return null;

  const tone = STATUS_TONE[transaction.status];
  const canRefund = transaction.status === "paid";
  const amountLabel = formatCurrency(
    transaction.amount,
    locale,
    transaction.currency,
  );
  const providerLabel = t(PROVIDER_BRAND_KEY[transaction.provider]);

  return (
    <section className="border-b border-[color:var(--hair)] px-4 py-[15px]">
      <div className="mb-[11px] flex items-center justify-between">
        <span className="text-[12.5px] font-bold text-[color:var(--text-primary)]">
          {t(keys.orders.onlinePaymentTitle)}
        </span>
        <span
          className="inline-flex items-center gap-1.5 rounded-[8px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-2.5 py-1 text-[11.5px] font-semibold"
          style={{ color: tone }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: tone }}
            aria-hidden="true"
          />
          {t(STATUS_KEY[transaction.status])}
        </span>
      </div>
      <div className="mb-[11px] -mx-4 h-px bg-[color:var(--hair)]" />
      <div className="mb-[9px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
        <span>{t(keys.orders.onlinePayProvider)}</span>
        <span className="font-semibold text-[color:var(--text-primary)]">
          {providerLabel}
        </span>
      </div>
      <div className="mb-[9px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
        <span>{t(keys.orders.onlinePayAmount)}</span>
        <span className="font-semibold text-[color:var(--text-primary)]">
          {amountLabel}
        </span>
      </div>
      {transaction.providerTxnId ? (
        <div className="flex items-center justify-between gap-3 text-[12.5px] text-[color:var(--text-secondary)]">
          <span>{t(keys.orders.onlinePayReference)}</span>
          <span
            className="truncate font-mono text-[11.5px] text-[color:var(--text-primary)]"
            dir="ltr"
          >
            {transaction.providerTxnId}
          </span>
        </div>
      ) : null}
      {canRefund ? (
        <button
          type="button"
          onClick={() => setRefundOpen(true)}
          className="mt-[13px] inline-flex h-9 items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-3.5 text-[12.5px] font-semibold text-[color:var(--text-primary)]"
        >
          {t(keys.orders.refundAction)}
        </button>
      ) : null}
      <RefundSheet
        isOpen={refundOpen}
        onClose={() => setRefundOpen(false)}
        storeSlug={storeSlug}
        orderId={order.id}
        providerLabel={providerLabel}
        maxAmount={transaction.amount}
        currency={transaction.currency}
      />
    </section>
  );
}
